import { Box, Column, DataRow, Divider, Link, Row, Tooltip, Typography } from '@tempmee/react';
import { SolidInformationCircleIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { EdtBadge } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/ShiftDetailsForm';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { formatFromMinuteToFormattedHour } from '~/utils/DateUtils';
import { EducationUtils } from '~/utils/EducationUtils';
import { getIsAllowOfficeToPostProcedure } from '~/utils/DentistUtils';
import { capitalizeEveryFirstLetter } from '~/utils/String';
import { type IJob } from '~/models';

/**
 * The job entity carries a couple of shift-detail fields not yet on the shared
 * IJob model (`procedures` lives on IShift; `autofillOptions` is a backend
 * field). Widen locally rather than mutating the shared model.
 */
type TShiftDetailsJob = IJob & {
  readonly procedures?: string[];
  readonly autofillOptions?: { favorite?: boolean };
};

interface IShiftDetailsInfoProps {
  job: TShiftDetailsJob;
}

const AutoConfirmTooltip = (
  <>
    Professional’s eligibility criteria:
    <br />
    <br />
    {'•  '}
    Has at least a {RemoteConfigUtils.getAutofillMinAttendanceScore()}% attendance score for
    previous shifts worked.
    <br />
    {'•  '}
    Has a minimum of 2 years working experience.
    <br />
    {'•  '}
    Has received exceptional performance ratings on over 95% of completed shifts.
    <br />
    {'•  '}
    Has completed at least 3 shifts.
    <br />
    <br />
    Note: You may change your auto-confirm preference at any time by visiting the Shift Details
    page.
  </>
);

/**
 * Right column of the redesigned open Shift Details page: the read-only shift
 * detail rows, rendered with the DS `DataRow` atom. Content matches the legacy
 * single-column details.
 */
export const ShiftDetailsInfo = ({ job }: IShiftDetailsInfoProps) => {
  const { subProfession, specialty, procedures, profession, professionName } = job;
  const professionTypeCode = UserProfession.getNormalizedCode(profession);
  const professionalType =
    (subProfession ? UserSubProfession.getName(subProfession) : undefined) ?? professionName;
  const showProcedures =
    getIsAllowOfficeToPostProcedure(professionalType, specialty) &&
    Boolean(procedures && procedures.length > 0);

  return (
    <Column gap={3} data-cy="shift-details-info" width="100%">
      <DataRow
        label="Office:"
        value={job.offer_owner.office_name}
        data-cy="shift-detail-office-name"
      />
      {job.isOnsiteInterview && job.postingTitle && (
        <DataRow
          label="Job Title:"
          data-cy="interview-detail-job-title"
          value={
            <Link
              href={`/permanent-jobs/job-overview/${job.postingId}`}
              color="primary"
              underline="always"
            >
              {job.postingTitle}
            </Link>
          }
        />
      )}
      <DataRow label="Date:" value={job.local_date} />
      <DataRow label="Professional Type:" value={professionalType} />
      <DataRow
        label="Preferred Specialty:"
        value={capitalizeEveryFirstLetter(specialty?.split('_')?.[1] || 'General')}
      />

      {professionTypeCode === UserProfession.RDH && (
        <DataRow label="Assisted Hygiene Available:" value={job.isAssistedHygiene ? 'Yes' : 'No'} />
      )}

      {showProcedures && (
        <Box py={2}>
          <Typography variant="body2" color={colors.neutral[600]} bold>
            Shift Procedure(s):
          </Typography>
          <Column gap={1} pt={2}>
            {EducationUtils.mapToRemoveProcedurePrefix(procedures ?? []).map((procedure) => (
              <Row key={procedure} align="start" gap={2}>
                <Typography as="span" variant="body1" color={colors.neutral[500]}>
                  {'•'}
                </Typography>
                <Typography as="span" variant="body1" color={colors.neutral[500]}>
                  {procedure}
                </Typography>
              </Row>
            ))}
          </Column>
          <Divider />
        </Box>
      )}

      <DataRow
        label="Hours:"
        icon={
          /*
           * Timezone tag — reuses the EdtBadge from the shift-request flow so
           * both surfaces render the identical pill. Slated for removal in the
           * next version; drop it then.
           */
          <EdtBadge>{job.local_timezone}</EdtBadge>
        }
        value={`${job.local_start_time} - ${job.local_end_time}`}
      />
      <DataRow label="Hourly Rate:" value={`$${(job.rate ?? 0).toFixed(2)}`} />
      <DataRow
        label="Meal Break:"
        value={`${formatFromMinuteToFormattedHour(Number(job.lunch_break_time))}${
          job.is_lunch_break_paid ? ' (Paid)' : ' (Unpaid)'
        }`}
      />

      {!job.isOnsiteInterview && (
        <DataRow
          label="Auto-Confirm:"
          divider={false}
          icon={
            <Tooltip content={AutoConfirmTooltip}>
              <SolidInformationCircleIcon color={colors.neutral[300]} />
            </Tooltip>
          }
          value={
            <Column gap={2} align="end">
              <Typography variant="body1" textAlign="right">
                Favorites / {job.autofillOptions?.favorite ? 'On' : 'Off'}
              </Typography>
              <Typography variant="body1" textAlign="right">
                Eligible Professionals / {job.autoFill ? 'On' : 'Off'}
              </Typography>
            </Column>
          }
        />
      )}
    </Column>
  );
};
